import React, { useState } from 'react';
import { ChevronRight, Search } from 'react-feather';
import * as Icons from 'react-feather';
import { docsCategories, searchDocs } from './docsData';

const DocsSidebar = ({ currentCategory, currentArticle, onNavigate, mobileDrawerOpen, onCloseDrawer }) => {
  const [expandedCategories, setExpandedCategories] = useState(
    currentCategory ? [currentCategory] : [docsCategories[0]?.slug]
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim().length > 1) {
      const results = searchDocs(query);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const toggleCategory = (slug) => {
    setExpandedCategories(prev =>
      prev.includes(slug)
        ? prev.filter(s => s !== slug)
        : [...prev, slug]
    );
  };

  const getIcon = (iconName, size = 18) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent size={size} /> : null;
  };

  const handleResultClick = (result) => {
    onNavigate(result.categorySlug, result.slug);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <aside
      className={`docs-sidebar ${mobileDrawerOpen ? 'docs-sidebar-open' : ''}`}
      style={{
        position: 'fixed',
        top: '64px',
        left: 0,
        bottom: 0,
        width: '300px',
        backgroundColor: '#ffffff',
        borderRight: '1px solid rgba(0, 0, 0, 0.06)',
        overflowY: 'auto',
        padding: '32px 24px',
        zIndex: 999,
        transition: 'transform 0.25s ease'
      }}
    >
      {/* Search in sidebar */}
      <div style={{ position: 'relative', marginBottom: '32px' }}>
        <Search
          size={16}
          style={{
            position: 'absolute',
            left: '14px',
            top: '12px',
            color: 'rgba(0, 0, 0, 0.35)'
          }}
        />
        <input
          type="text"
          placeholder="Search help"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 14px 12px 42px',
            fontSize: '0.9rem',
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            color: '#1a1a1a',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />

        {/* Search Results Dropdown */}
        {searchResults.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '8px',
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            maxHeight: '320px',
            overflowY: 'auto',
            zIndex: 100
          }}>
            {searchResults.slice(0, 8).map((result, index) => (
              <button
                key={`${result.categorySlug}-${result.slug}`}
                onClick={() => handleResultClick(result)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: index < Math.min(searchResults.length, 8) - 1 ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.03)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div style={{
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  color: '#1a1a1a',
                  marginBottom: '4px'
                }}>
                  {result.title}
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: 'rgba(0, 0, 0, 0.45)'
                }}>
                  {result.categoryTitle}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* No results message */}
        {searchQuery.trim().length > 1 && searchResults.length === 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '8px',
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            padding: '20px 16px',
            textAlign: 'center',
            zIndex: 100
          }}>
            <div style={{
              fontSize: '0.9rem',
              color: 'rgba(0, 0, 0, 0.5)'
            }}>
              No results found for "{searchQuery}"
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav>
        {docsCategories.map((category, catIndex) => {
          const isExpanded = expandedCategories.includes(category.slug);
          const isCurrent = currentCategory === category.slug;

          return (
            <div key={category.slug} style={{ marginBottom: catIndex < docsCategories.length - 1 ? '24px' : '0' }}>
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.slug)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '8px 0',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: '#1a1a1a',
                  textAlign: 'left',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                <span style={{
                  color: 'rgba(0, 0, 0, 0.4)',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {getIcon(category.icon, 16)}
                </span>
                <span style={{ flex: 1 }}>{category.title}</span>
              </button>

              {/* Articles List */}
              <div style={{ marginTop: '8px' }}>
                {category.articles.map((article) => {
                  const isActive = currentCategory === category.slug && currentArticle === article.slug;

                  return (
                    <button
                      key={article.slug}
                      onClick={() => onNavigate(category.slug, article.slug)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 0 10px 28px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: isActive ? '500' : '400',
                        color: isActive ? '#1a1a1a' : 'rgba(0, 0, 0, 0.6)',
                        textAlign: 'left',
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.color = '#1a1a1a';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                        }
                      }}
                    >
                      <ChevronRight size={14} style={{ color: 'rgba(0, 0, 0, 0.3)' }} />
                      {article.title}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Mobile responsive overrides */}
      <style>{`
        @media (max-width: 768px) {
          .docs-sidebar {
            top: 56px !important;
            width: 280px !important;
            transform: translateX(-100%);
            box-shadow: 4px 0 24px rgba(0, 0, 0, 0.08);
          }
          .docs-sidebar.docs-sidebar-open {
            transform: translateX(0);
          }
        }
      `}</style>
    </aside>
  );
};

export default DocsSidebar;